import { create } from 'zustand';
import { User } from 'firebase/auth';
import { savePrompt, saveContext, saveTeaching } from './firebase';
import { saveApiKeys, ApiKeys } from './firebase/apiKeys';

// ... (previous interfaces remain the same)

interface StoreState {
  user: User | null;
  recording: boolean;
  aiInteractionEnabled: boolean;
  currentTranscript: string;
  teachings: Teaching[];
  currentTeaching: Teaching | null;
  conversations: Record<string, Conversation[]>;
  teachingContext: TeachingContext;
  prompts: Record<string, string>;
  apiKeys: ApiKeys | null;
  apiKeysError: string | null;
  apiKeysUnsubscribe: (() => void) | null;
  setUser: (user: User | null) => void;
  setRecording: (recording: boolean) => void;
  setAiInteractionEnabled: (enabled: boolean) => void;
  setCurrentTranscript: (transcript: string) => void;
  setTeachingContext: (context: TeachingContext) => Promise<void>;
  addTeaching: (teaching: Teaching) => void;
  updateCurrentTeaching: (summary: string) => void;
  addConversation: (teachingId: string, conversation: Conversation) => void;
  updatePrompt: (type: string, content: string) => Promise<void>;
  loadPrompts: (userId: string) => Promise<void>;
  updateApiKeys: (keys: ApiKeys) => Promise<void>;
  setApiKeys: (keys: ApiKeys | null) => void;
  setApiKeysError: (error: string | null) => void;
  setApiKeysUnsubscribe: (unsubscribe: (() => void) | null) => void;
  hasValidApiKeys: () => boolean;
}

export const useStore = create<StoreState>((set, get) => ({
  user: null,
  recording: false,
  aiInteractionEnabled: true,
  currentTranscript: '',
  teachings: [],
  currentTeaching: null,
  conversations: {},
  teachingContext: {
    audience: '',
    objectives: '',
    priorKnowledge: '',
    additionalContext: '',
  },
  prompts: {},
  apiKeys: null,
  apiKeysError: null,
  apiKeysUnsubscribe: null,
  
  setUser: (user) => set({ user }),
  setRecording: (recording) => set({ recording }),
  setAiInteractionEnabled: (enabled) => set({ aiInteractionEnabled: enabled }),
  setCurrentTranscript: (transcript) => set({ currentTranscript: transcript }),
  
  setTeachingContext: async (context) => {
    const { user } = get();
    if (user) {
      await saveContext(user.uid, context);
    }
    set({ teachingContext: context });
  },
  
  addTeaching: (teaching) => {
    const { user } = get();
    if (user) {
      saveTeaching(user.uid, teaching);
    }
    set((state) => ({
      teachings: [...state.teachings, teaching],
      currentTeaching: teaching,
      currentTranscript: ''
    }));
  },
  
  updateCurrentTeaching: (summary) => set((state) => ({
    teachings: state.teachings.map((teaching, index) => 
      index === state.teachings.length - 1 
        ? { ...teaching, summary }
        : teaching
    )
  })),
  
  addConversation: (teachingId, conversation) => set((state) => ({
    conversations: {
      ...state.conversations,
      [teachingId]: [...(state.conversations[teachingId] || []), conversation]
    }
  })),
  
  updatePrompt: async (type, content) => {
    const { user } = get();
    if (user) {
      await savePrompt(user.uid, type, content);
      set((state) => ({
        prompts: {
          ...state.prompts,
          [type]: content
        }
      }));
    }
  },
  
  loadPrompts: async (userId) => {
    try {
      const userPrompts = await Promise.all([
        'reel',
        'long',
        'short'
      ].map(async (type) => {
        const prompt = await savePrompt(userId, type, '');
        return [type, prompt];
      }));

      set({
        prompts: Object.fromEntries(userPrompts)
      });
    } catch (error) {
      console.error('Error loading prompts:', error);
    }
  },
  
  updateApiKeys: async (keys: ApiKeys) => {
    const { user } = get();
    if (user) {
      try {
        await saveApiKeys(user.uid, keys);
        set({ 
          apiKeys: keys,
          apiKeysError: null 
        });
      } catch (error) {
        set({ apiKeysError: 'Failed to save API keys' });
        throw error;
      }
    }
  },
  
  setApiKeys: (keys) => set({ apiKeys: keys }),
  setApiKeysError: (error) => set({ apiKeysError: error }),
  setApiKeysUnsubscribe: (unsubscribe) => set({ apiKeysUnsubscribe: unsubscribe }),
  
  hasValidApiKeys: () => {
    const { apiKeys } = get();
    return !!(apiKeys?.openai && apiKeys?.elevenlabs);
  }
}));